import lodash from 'lodash';
import { getRegionAndFormattedSymbol } from '../common-helper';
import { IWidgetCustomization } from '../customization';
import Combination from './combination';
import { CombinationTextGeneration } from './combination-text-generation';
import DateTimeHelper from './date-time-helper';
import { IncomeCombination } from './income-combination';
import { IChecklistItem } from './strategy-helper';
import { TradeTicket } from './trade-ticket';

/*
    Common function to create trade ticket object to be passed in activity log.
*/
export const createTradeTicketLogObject = (
  combination: Combination | IncomeCombination,
  customization: IWidgetCustomization,
) => {
  let tradeTicket = new TradeTicket();
  tradeTicket.refreshTrade(combination, undefined);

  const options = (tradeTicket.optionLegs || []).map((p) => {
    return {
      ask: p.ask,
      bid: p.bid,
      price: p.price,
      quantity: p.quantity,
      expiry: DateTimeHelper.resolveExpiry(p.expiry).toISOString(),
      strike: p.strike,
      actionName: p.getActionName(),
      isToClose: p.isToClose,
      type: p.type,
      showShortCodeInTradeTicket: p.showShortCodeInTradeTicket,
    };
  });
  const securities = (tradeTicket.securityLegs || []).map((p) => {
    return {
      ask: p.ask,
      bid: p.bid,
      price: p.price,
      quantity: p.quantity,
      actionName: p.getActionName(),
      isToClose: p.isToClose,
      type: p.type,
      showShortCodeInTradeTicket: p.showShortCodeInTradeTicket,
    };
  });
  /**
   * !Important
   * Condition for Income covered call
   * If canadian / US symbol loaded in covered call
   * strategy name should be sent as "Call" as from OP only call leg is sent in post event.
   * Reason is in post event Call option is only sent as
   * in OP asuumption is shares are owned.
   */
  let strategyName = tradeTicket.strategyName;
  if (
    !customization.allowStockPlusOptionforCASymbol &&
    tradeTicket.combination.isIncome &&
    tradeTicket.strategyName === 'Covered Call'
  ) {
    strategyName = 'Call';
  }

  if (
    !combination.isIncomeStrategy &&
    customization.isEmbeddingPlatform &&
    combination.fullNameWithoutSymbol().includes('Buy Write')
  ) {
    strategyName = 'Buy Write';
  }

  const price = tradeTicket.combination.costWithoutOwned();

  const { symbol, formattedSymbolByRegion, region } = getRegionAndFormattedSymbol(tradeTicket.combination.symbol);

  const tradeTicketObj = {
    symbol: formattedSymbolByRegion,
    globalSymbol: symbol,
    optionLegs: options,
    securityLegs: securities,
    strategyName: strategyName,
    price: price,
    unitPrice: Math.abs(tradeTicket.avgPremium()),
    priceSign: Math.sign(price),
    isCustomStrategy: !!tradeTicket.eventStrategyName.match(/custom strategy/i),
    region: region,
  };

  return tradeTicketObj;
};

/**
 * This function handles extra details about a trade that are not part of the standard trade legs.
 * It calculates things like the strategy title, max risk, max reward, and expiry days.
 * It also checks if we should show a warning for "Naked Puts".
 *
 * @param combination - The trade or income strategy being analyzed.
 * @param checkListItems - (Optional) Any checklist results (like POW or Score) to include in the log.
 * @param resultingCombination - (Optional) For portfolio changes, this is the final state after the trade.
 */
export const createAdditionalTradeLogObject = (
  combination: Combination | IncomeCombination,
  checkListItems: IChecklistItem[] = [],
  resultingCombination?: Combination | IncomeCombination | any,
) => {
  const title = CombinationTextGeneration.generateTradeTicketTitle(combination);
  const isNakedPut = combination.isNakedPut();
  const showNakedPutWarning = isNakedPut && title.length > 0 && title[0].includes('Sell to Open');

  const targetCombination = resultingCombination || combination;

  return {
    title: title.length > 0 ? title[0] : '',
    strategyChecklist: lodash.uniqBy(checkListItems, 'type'),
    reward: targetCombination.maxReward(),
    risk: targetCombination.maxRisk(),
    showNakedPutWarning: showNakedPutWarning,
    daysToExpiry: targetCombination.daysToExpiry(),
    isNakedPut: isNakedPut,
  };
};

/**
 * This is the main function to use for logging a trade.
 * It combines the standard trade ticket data (legs, price, strategy name)
 * WITH the additional strategy details (risk, reward, titles, checklist).
 *
 * Use this when you want to send a complete snapshot of a trade to the activity log or a host platform.
 */
export const createFullTradeLogObject = (
  combination: Combination | IncomeCombination,
  customization: IWidgetCustomization,
  checkListItems: IChecklistItem[] = [],
  resultingCombination?: Combination | IncomeCombination | any,
) => {
  const tradeTicketObj = createTradeTicketLogObject(combination, customization);
  const additionalObj = createAdditionalTradeLogObject(combination, checkListItems, resultingCombination);

  return {
    ...additionalObj,
    ...tradeTicketObj,
  };
};
